import { useEffect, useRef, useState } from 'react';

export type UseSSEOptions = {
  url: string | (() => string);
  onMessage?: (data: unknown) => void;
};

export const useSSE = ({ url, onMessage }: UseSSEOptions) => {
  const [data, setData] = useState<unknown>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const resolvedUrl = typeof url === 'function' ? url() : url;
    const fullUrl = resolvedUrl ? `${process.env.REACT_APP_API_ENDPOINT}/${resolvedUrl}` : '';
    if (!fullUrl) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const eventSource = new EventSource(fullUrl);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        setError(null);
      };

      eventSource.onmessage = event => {
        try {
          const newData = JSON.parse(event.data);
          setData(newData);
          if (onMessage) {
            onMessage(newData);
          }
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Error parsing SSE data'));
        }
      };

      eventSource.onerror = () => {
        setIsConnected(false);
        setError(new Error('SSE connection error'));
        eventSource.close();
        eventSourceRef.current = null;
        setTimeout(() => {
          if (!eventSourceRef.current) {
            // Optionally, you could implement auto-reconnect logic here
          }
        }, 5000);
      };

      return () => {
        eventSource.close();
        eventSourceRef.current = null;
      };
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown SSE error'));
    }
  }, [url, onMessage]);

  return { data, error, isConnected };
};
